// src/components/Auth/ResetPassword.jsx

import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = query.get('token') || '';
  const emailFromQuery = query.get('email') || '';

  const [form, setForm] = useState({
    email: emailFromQuery,
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('');
    setError('');
    setLoading(true);

    try {
      const response = await axios.post('/auth/reset-password', {
        ...form,
        token,
      });
      setStatus(response.data.message || 'Password reset successfully.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <section className="auth-hero">
        <div className="auth-hero-content">
          <div className="badge badge-muted" style={{ color: 'white' }}>
            Security
          </div>
          <h1 style={{ fontSize: '3rem', lineHeight: 1.1, margin: '1.5rem 0' }}>
            Choose a new password
          </h1>
          <p style={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.85)' }}>
            Reset links are time-bound for your safety. Pick a strong password to protect your account.
          </p>
        </div>
      </section>

      <section className="auth-panel">
        <div className="auth-card">
          <header style={{ marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.3rem' }}>Reset password</h2>
            <p className="text-muted">
              Enter your email and a new password. We will update your account immediately.
            </p>
          </header>

          {status && <div className="success-banner">{status}</div>}
          {error && <div className="error-banner">{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
            <div className="field">
              <label htmlFor="email">Work email</label>
              <input
                id="email"
                name="email"
                type="email"
                className="input"
                value={form.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="field">
              <label htmlFor="password">New password</label>
              <div className="input-with-action">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="input-action"
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="password_confirmation">Confirm password</label>
              <div className="input-with-action">
                <input
                  id="password_confirmation"
                  name="password_confirmation"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="input"
                  value={form.password_confirmation}
                  onChange={handleChange}
                  placeholder="Repeat password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  className="input-action"
                  aria-pressed={showConfirmPassword}
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || !token}>
              {loading ? 'Updating...' : 'Update password'}
            </button>
            {!token && (
              <p className="error-banner" role="alert">
                This reset link is missing a token. Please request a new reset email.
              </p>
            )}
          </form>

          <p className="text-muted" style={{ marginTop: '1.5rem', fontSize: '0.9rem' }}>
            <Link to="/login" style={{ fontWeight: 600 }}>Back to sign in</Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
